import React, { useEffect, useState } from "react";
import axios from "axios";
import TopNav from "../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/forms/FormField";
import TextInput from "../components/forms/TextInput";
import StatusMessage from "../components/ui/StatusMessage";

import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const shellStyle = {
  maxWidth: "520px",
  margin: "0 auto",
  paddingTop: "2.5rem",
};

const heroStyle = {
  textAlign: "center",
  marginBottom: "1.5rem",
};

const brandTitleStyle = {
  margin: 0,
  fontSize: "2.6rem",
  fontWeight: 800,
  lineHeight: 1.05,
  letterSpacing: "0.3px",
};

const subtitleStyle = {
  margin: "0.75rem 0 0",
  fontSize: "1.05rem",
  fontWeight: 600,
};

const supportingTextStyle = {
  margin: "0.4rem 0 0",
  fontSize: "0.95rem",
};

const languageSwitcherStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  marginTop: "12px",
};

const getLangButtonStyle = (isActive) => ({
  border: "1px solid var(--color-border)",
  background: isActive
    ? "var(--color-accent)"
    : "var(--color-surface, white)",
  color: isActive ? "white" : "var(--color-text)",
  borderRadius: "8px",
  padding: "6px 12px",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: "0.9rem",
  minWidth: "52px",
  transition: "all 0.2s ease",
});

const footerStyle = {
  marginTop: "1rem",
  textAlign: "center",
};

function inferHomePath(user) {
  const role = String(user?.role || "").toLowerCase();

  if (role === "admin") return "/admin";
  if (role === "payer") return "/payer";
  if (role === "vendor") return "/vendor";

  return "/login";
}

export default function Login() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    user,
    loadingUser,
    applySession,
    clearStoredSession,
  } = useAuth();

  const [userno, setuserno] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [verdict, setVerdict] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    clearStoredSession();
  }, [clearStoredSession]);

  useEffect(() => {
    if (!loadingUser && user) {
      navigate(inferHomePath(user), { replace: true });
    }
  }, [user, loadingUser, navigate]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem("lang", lng);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isLoading) return;

    if (!userno.trim() || !password.trim()) {
      setMessage(t("login.messages.invalid_credentials"));
      setVerdict("cancelled");
      return;
    }

    try {
      setIsLoading(true);
      setMessage("");
      setVerdict("");

      clearStoredSession();

      console.log(
        "FULL URL:",
        `${process.env.REACT_APP_API_URL}/auth/login`
      );

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        {
          userno: userno.trim(),
          password: password.trim(),
        }
      );

      const { token, user: loggedInUser } = res.data;

      await applySession({
        token,
        role: loggedInUser.role,
      });

      navigate(inferHomePath(loggedInUser), {
        replace: true,
      });
    } catch (err) {
      setMessage(t("login.messages.invalid_credentials"));
      setVerdict("cancelled");
    } finally {
      setIsLoading(false);
    }
  };

  const messageVariant =
    verdict === "approved"
      ? "success"
      : message
      ? "error"
      : "info";

  if (loadingUser) return null;

  return (
    <StandardScreen>
      <TopNav />

      <ScreenContent>
        <div style={shellStyle}>
          <div style={heroStyle}>

            {/* Brand heading now matches Register page */}
            <h1 style={brandTitleStyle}>
              <span style={{ color: "var(--color-text)" }}>
                bas
              </span>
              <span style={{ color: "var(--color-accent)" }}>
                -bin
              </span>
            </h1>

            <p style={subtitleStyle}>
              {t("login.punchy_title")}
            </p>

            <p style={supportingTextStyle}>
              {t("login.punchy_subtitle")}
            </p>

            {/* Language switcher now matches Register page */}
            <div style={languageSwitcherStyle}>
              <button
                type="button"
                onClick={() => changeLanguage("en")}
                style={getLangButtonStyle(
                  i18n.language
                    ?.toLowerCase()
                    .startsWith("en")
                )}
              >
                EN
              </button>

              <button
                type="button"
                onClick={() => changeLanguage("tr")}
                style={getLangButtonStyle(
                  i18n.language
                    ?.toLowerCase()
                    .startsWith("tr")
                )}
              >
                TR
              </button>
            </div>
          </div>

          <Card title={t("login.title")}>
            <form onSubmit={handleLogin}>
              <Stack gap="4">
                <FormField
                  label={t("login.fields.userno.label")}
                >
                  <TextInput
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={userno}
                    onChange={(e) =>
                      setuserno(
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    placeholder={t(
                      "login.fields.userno.placeholder"
                    )}
                    disabled={isLoading}
                  />
                </FormField>

                <FormField
                  label={t("login.fields.password.label")}
                >
                  <TextInput
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={password}
                    onChange={(e) =>
                      setPassword(
                        e.target.value.replace(/\D/g, "")
                      )
                    }
                    placeholder={t(
                      "login.fields.password.placeholder"
                    )}
                    disabled={isLoading}
                  />
                </FormField>

                <Button
                  type="submit"
                  fullWidth
                  disabled={isLoading}
                >
                  {t("login.actions.submit")}
                </Button>

                <StatusMessage
                  variant={messageVariant}
                  message={message}
                />
              </Stack>
            </form>
          </Card>

          <div style={footerStyle}>
            <p>
              {t("login.links.no_account")}{" "}
              <Link to="/register">
                {t("login.links.register_here")}
              </Link>
            </p>
          </div>

          <div style={footerStyle}>
            <p>
              {t("login.links.return_back")}{" "}
              <Link to="/">
                {t("login.links.return_here")}
              </Link>
            </p>
          </div>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}